import type { ScoringConfig } from "../config/types";
import type { GameEvents } from "../core/events";
import type { RunState } from "../core/runState";

export type ScoreState = Pick<
    RunState,
    | "score" | "styleScore" | "combo" | "longestCombo" | "jumpTricks" | "tricksLanded"
    | "crashes" | "airtimeS" | "bestSpeed" | "zonesReached" | "escaped"
>;

type Handled = "trickComplete" | "land" | "trickFail" | "wipeout" | "zoneChange" | "skinnyEnd" | "runEnd";
export type ScoringEvent = { [E in Handled]: { name: E; payload: GameEvents[E] } }[Handled];
export const SCORING_EVENTS: Handled[] = ["trickComplete", "land", "trickFail", "wipeout", "zoneChange", "skinnyEnd", "runEnd"];

const lose = (s: ScoreState, points: number): ScoreState => ({ ...s, score: Math.max(0, s.score - points) });

function bumpCombo(s: ScoreState, cfg: ScoringConfig): ScoreState {
    const combo = Math.min(cfg.comboCap, s.combo + cfg.comboStep);
    return { ...s, combo, longestCombo: Math.max(s.longestCombo, combo) };
}

function crash(s: ScoreState, penalty: number): ScoreState {
    return { ...lose(s, penalty), combo: 1, jumpTricks: 0, crashes: s.crashes + 1 };
}

export function speedMultiplier(speed: number, cfg: ScoringConfig): number {
    const { fromSpeed, toSpeed, maxMultiplier } = cfg.speedBonus;
    const t = Math.min(1, Math.max(0, (speed - fromSpeed) / (toSpeed - fromSpeed)));
    return 1 + t * (maxMultiplier - 1);
}

export function applyEvent(s: ScoreState, event: ScoringEvent, cfg: ScoringConfig): ScoreState {
    switch (event.name) {
        case "trickComplete": {
            const points = event.payload.value * s.combo;
            return { ...s, score: s.score + points, styleScore: s.styleScore + points, jumpTricks: s.jumpTricks + 1 };
        }
        case "land": {
            const landed = { ...s, airtimeS: s.airtimeS + event.payload.airtime, jumpTricks: 0 };
            if (!event.payload.clean || s.jumpTricks === 0) return landed;
            return bumpCombo({ ...landed, tricksLanded: s.tricksLanded + s.jumpTricks }, cfg);
        }
        case "trickFail":
            return crash(s, cfg.failedLanding);
        case "wipeout":
            if (event.payload.severity === "small") return s;
            return crash(s, cfg.crashPenalty[event.payload.severity]);
        case "zoneChange":
            if (s.zonesReached.includes(event.payload.zone)) return s;
            return { ...s, zonesReached: [...s.zonesReached, event.payload.zone] };
        case "skinnyEnd":
            return event.payload.clean ? bumpCombo(s, cfg) : s;
        case "runEnd":
            if (event.payload.reason !== "escaped") return s;
            return { ...s, escaped: true, score: s.score + cfg.escapeBonus };
    }
}

export function tick(s: ScoreState, speed: number, downhillM: number, scoreDistance: boolean, cfg: ScoringConfig): ScoreState {
    const distancePoints = scoreDistance ? Math.max(0, downhillM) * cfg.distancePerM * speedMultiplier(speed, cfg) : 0;
    return { ...s, bestSpeed: Math.max(s.bestSpeed, speed), score: s.score + distancePoints };
}
